import { useState } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const STATE_COLORS = {
  aggregation_not_branched: { border: 'border-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/20', text: 'text-amber-700 dark:text-amber-400', icon: AlertTriangle },
  config_not_propagated: { border: 'border-red-500', bg: 'bg-red-50 dark:bg-red-950/20', text: 'text-red-700 dark:text-red-400', icon: XCircle },
  both_gapped: { border: 'border-red-500', bg: 'bg-red-50 dark:bg-red-950/20', text: 'text-red-700 dark:text-red-400', icon: XCircle },
  all_wired: { border: 'border-green-500', bg: 'bg-green-50 dark:bg-green-950/20', text: 'text-green-700 dark:text-green-400', icon: CheckCircle2 },
};

function fmt(v) {
  if (v == null) return '--';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(v) {
  if (v == null) return '--';
  return `${(Number(v) * 100).toFixed(1)}%`;
}

export function ManagedPropertyAttributionPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.managedPropertyAttribution();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const archState = results?.architectural_state || 'all_wired';
  const colors = STATE_COLORS[archState] || STATE_COLORS.all_wired;
  const StateIcon = colors.icon;

  const cfg = results?.augereau_config;
  const res = results?.augereau_reservations;
  const impact = results?.france_revenue_impact_ytd;
  const scan = results?.managed_properties_portfolio_scan || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-500" />
          Managed Property Revenue Attribution
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          For managed properties, France/UK revenue should reflect only the management fee,
          not gross. Reads the Integrations config, checks whether bookings inherit the
          managed tag and rate, and computes the YTD overstatement from Augereau Suite and
          any other managed properties.
        </p>

        <Button onClick={runAudit} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Run Audit
        </Button>

        {results && (
          <div className="space-y-6">
            {/* Architectural state banner */}
            <div className={`flex items-start gap-2 rounded-lg border ${colors.border} ${colors.bg} p-3`}>
              <StateIcon className={`h-5 w-5 mt-0.5 shrink-0 ${colors.text}`} />
              <div className="text-sm">
                <p className={`font-medium ${colors.text}`}>
                  Architectural state: <code>{results.architectural_state}</code>
                </p>
                {results.verdict && (
                  <div className="text-muted-foreground mt-1 space-y-1">
                    {results.verdict.split(' | ').map((part, i) => (
                      <p key={i}>{part}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Headline overstatement */}
            {results.total_managed_overstatement_ytd != null && (
              <div className="rounded-lg border p-4 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Total YTD managed-property overstatement
                </p>
                <p className="text-3xl font-bold tabular-nums mt-1">
                  {fmt(results.total_managed_overstatement_ytd)} EUR
                </p>
              </div>
            )}

            {/* Part A: Augereau config */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Part A: Augereau Config</h3>
              {cfg && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Property</p>
                    <p className="text-sm font-medium">{cfg.property_name || '--'}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Configured managed?</p>
                    <Badge variant={cfg.configured_as_managed ? 'default' : 'secondary'}>
                      {cfg.configured_as_managed ? 'yes' : 'no'}
                    </Badge>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Management fee rate</p>
                    <p className="text-xl font-bold tabular-nums">{pct(cfg.management_fee_rate)}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Config source</p>
                    <p className="text-sm font-mono">{cfg.config_source || '--'}</p>
                  </div>
                </div>
              )}
              {cfg?.note && (
                <p className="text-sm text-amber-600">{cfg.note}</p>
              )}
            </div>

            {/* Part B: Augereau reservations */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Part B: Augereau Reservations</h3>
              {res && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Total</p>
                      <p className="text-xl font-bold tabular-nums">{res.total}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Tagged managed</p>
                      <p className="text-xl font-bold tabular-nums">{res.managed_tag_count}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Tagged owned</p>
                      <p className="text-xl font-bold tabular-nums">{res.owned_tag_count}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Rate populated</p>
                      <p className="text-xl font-bold tabular-nums">{res.rate_populated_count}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Fee populated</p>
                      <p className="text-xl font-bold tabular-nums">{res.fee_populated_count}</p>
                    </div>
                  </div>

                  {res.sample_reservations?.length > 0 && (
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-3 py-2 font-medium">ID</th>
                            <th className="text-left px-3 py-2 font-medium">Channel</th>
                            <th className="text-left px-3 py-2 font-medium">Check-in</th>
                            <th className="text-right px-3 py-2 font-medium">Nights</th>
                            <th className="text-right px-3 py-2 font-medium">Total price</th>
                            <th className="text-right px-3 py-2 font-medium">Net rev</th>
                            <th className="text-left px-3 py-2 font-medium">Mgmt type</th>
                            <th className="text-right px-3 py-2 font-medium">Fee rate</th>
                            <th className="text-right px-3 py-2 font-medium">Mgmt fee</th>
                            <th className="text-left px-3 py-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {res.sample_reservations.map((r, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="px-3 py-2 font-mono text-xs">
                                {r.uplisting_id || r.smoobu_id}
                              </td>
                              <td className="px-3 py-2 text-xs">{r.channel}</td>
                              <td className="px-3 py-2 text-xs">{r.check_in}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{r.nights}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmt(r.total_price)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmt(r.net_revenue)}</td>
                              <td className="px-3 py-2">
                                <Badge variant={r.management_type === 'managed' ? 'default' : 'secondary'}>
                                  {r.management_type || '--'}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{pct(r.management_fee_rate)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmt(r.management_fee)}</td>
                              <td className="px-3 py-2">
                                <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Part C: France revenue impact */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Part C: France Revenue Impact (YTD)</h3>
              {impact && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Current contribution</p>
                      <p className="text-xl font-bold tabular-nums">
                        {impact.current_contribution_eur != null ? fmt(impact.current_contribution_eur) : '--'}
                      </p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Correct contribution</p>
                      <p className="text-xl font-bold tabular-nums">
                        {impact.correct_contribution_eur != null ? fmt(impact.correct_contribution_eur) : '--'}
                      </p>
                    </div>
                    <div className={`rounded-lg border p-3 ${impact.overstatement_eur > 0 ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : ''}`}>
                      <p className="text-xs text-muted-foreground">Overstatement</p>
                      <p className={`text-xl font-bold tabular-nums ${impact.overstatement_eur > 0 ? 'text-red-600' : ''}`}>
                        {impact.overstatement_eur != null ? fmt(impact.overstatement_eur) : '--'}
                      </p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Bookings</p>
                      <p className="text-xl font-bold tabular-nums">{impact.booking_count ?? '--'}</p>
                    </div>
                  </div>
                  {impact.note && (
                    <p className="text-sm text-amber-600">{impact.note}</p>
                  )}
                  {impact.ytd_window && (
                    <p className="text-xs text-muted-foreground">Window: {impact.ytd_window}</p>
                  )}
                </>
              )}
            </div>

            {/* Part D: Portfolio scan */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Part D: Managed Properties Portfolio Scan</h3>
              {scan.length === 0 ? (
                <p className="text-sm text-muted-foreground">No properties configured as managed.</p>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                        <th className="text-left px-3 py-2 font-medium">Platform</th>
                        <th className="text-left px-3 py-2 font-medium">Region</th>
                        <th className="text-right px-3 py-2 font-medium">Rate</th>
                        <th className="text-right px-3 py-2 font-medium">Reservations</th>
                        <th className="text-right px-3 py-2 font-medium">Gross YTD</th>
                        <th className="text-right px-3 py-2 font-medium">Mgmt fee YTD</th>
                        <th className="text-right px-3 py-2 font-medium">Overstatement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scan.map((mp, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2 text-xs">{mp.name}</td>
                          <td className="px-3 py-2 text-xs">{mp.platform}</td>
                          <td className="px-3 py-2 text-xs uppercase">{mp.region}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{pct(mp.rate)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{mp.reservation_count}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(mp.gross_ytd)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(mp.management_fee_ytd)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-medium ${Number(mp.overstatement_ytd) > 0 ? 'text-red-600' : ''}`}>
                            {fmt(mp.overstatement_ytd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
